import { useSyncExternalStore } from 'react';

/**
 * Tiny feature-local external store tracking whether the vanilla-cookieconsent
 * consent modal is currently visible.
 *
 * The consent modal's DOM (and its show/hide lifecycle) is owned by the
 * vanilla-cookieconsent library, not React. CookieConsent.tsx wires the
 * library's `onModalShow` / `onModalHide` callbacks (scoped to
 * `modalName === 'consentModal'`) to `setConsentModalOpen`, and AppFooter reads
 * the state via `useConsentModalOpen` so its shield button can toggle the modal
 * (open ⇄ close) rather than only ever opening it.
 *
 * Kept local to the cookie-consent feature (not in the global Zustand store)
 * because it is a single transient UI flag owned by a third-party library.
 */

let isOpen = false;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) {
    listener();
  }
}

/** Update the tracked open/closed state and notify subscribers. */
export function setConsentModalOpen(open: boolean): void {
  if (isOpen === open) {
    return;
  }
  isOpen = open;
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): boolean {
  return isOpen;
}

/** React hook: `true` while the consent modal is visible, `false` otherwise. */
export function useConsentModalOpen(): boolean {
  // getSnapshot returns a primitive boolean, so it is a stable server/client snapshot.
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
